import type { SelectOptionGroup } from '@/app/components/forms/select';
import type {
  CampaignModel,
  CitizenType,
} from '@/app/lcv/components/campaigns/big-campaign-2026/actions/schema';
import {
  CAMPAIGN_NAME,
  MODEL_GROUP_LABEL,
  NON_OMO_BRANCHES,
} from '@/app/lcv/components/campaigns/big-campaign-2026/constants';
import type {
  DiscountPriceType,
  EligibilityType,
  ModelGroupType,
  TierType,
} from '@/app/lcv/components/campaigns/big-campaign-2026/types';
import { formatDateFull } from '@/app/utils/format';

export const formatPriceThai = (val: number, unit: boolean = false) =>
  `${(+val || 0).toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}${unit ? ' บาท' : ''}`;

/** วันที่กดรับสิทธิ์: this visit's lead `createdAt`, else `confirmedAt`, else today. */
export const formatClaimDate = ({
  leadCreatedAt,
  confirmedAt,
}: Pick<EligibilityType, 'leadCreatedAt' | 'confirmedAt'>) => {
  const source = leadCreatedAt || confirmedAt;
  const date = source ? new Date(source) : new Date();
  return formatDateFull(Number.isNaN(date.getTime()) ? new Date() : date);
};

export const createBranchCodeFormat = (code: string) => `00${code}`;

/** `?branchCode=` (8 digits, optionally `00`-prefixed) → the dealer code, else `undefined`. */
export const parseBranchCodeParam = (value?: string) =>
  /^(?:00)?(\d{8})$/.exec(value?.trim() ?? '')?.[1];

export const getDisplayName = (fullname: string) => {
  if (!fullname) return '';
  return `คุณ${fullname.split(' ')[0]}`;
};

/**
 * Coupon ID line: last 4 characters only (national ID or passport). The coupon
 * is shared, and the leading ID digits reveal the registration area.
 */
const NATIONAL_ID_MASK_CHAR = 'x';
const PASSPORT_MASK_CHAR = '*';
const VISIBLE_SUFFIX = 4;

const maskAllButLast = (value: string, maskChar: string) => {
  const masked = Math.max(value.length - VISIBLE_SUFFIX, 0);
  return maskChar.repeat(masked) + value.slice(masked);
};

export const maskCitizenId = (value?: string, type?: CitizenType): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '';

  return maskAllButLast(
    raw,
    type === 'passport' ? PASSPORT_MASK_CHAR : NATIONAL_ID_MASK_CHAR
  );
};

export const CITIZEN_TYPE_LABEL: Record<CitizenType, string> = {
  national_id: 'เลขบัตรประชาชน',
  passport: 'เลขหนังสือเดินทาง',
};

export const findModelByCode = (
  models: CampaignModel[],
  modelCode?: string
): CampaignModel | undefined => {
  if (!modelCode) return undefined;
  return models.find((item) => item.modelCode === modelCode);
};

/** API `modelGroup` (key or Thai name) → discount key. */
const MODEL_GROUP_KEYS: Record<string, ModelGroupType> = {
  pickup: 'pickup',
  ppv: 'ppv',
  'กลุ่มรถกระบะขับเคลื่อน 2 ล้อ': 'pickup',
  'กลุ่มรถกระบะขับเคลื่อน 4 ล้อ': 'pickup',
  'กลุ่มรถครอบครัว 7 ที่นั่ง': 'ppv',
};

/** Anything unknown falls back to `pickup` so the coupon still renders. */
export const toModelGroup = (modelGroup?: string): ModelGroupType =>
  MODEL_GROUP_KEYS[modelGroup?.trim() ?? ''] ?? 'pickup';

export const getDiscountByModel = (
  discounts: EligibilityType['discounts'],
  modelGroup?: string
): DiscountPriceType | undefined => discounts?.[toModelGroup(modelGroup)];

/** Receiver coupon: own campaign discount + the sharer's my-MEMBER (fallback: shared group). */
export const buildShareReceiverDiscount = ({
  receiverDiscounts,
  sharerDiscounts,
  modelGroup,
  sharerModelGroup,
}: {
  receiverDiscounts: EligibilityType['discounts'];
  sharerDiscounts: EligibilityType['discounts'][];
  modelGroup?: string;
  sharerModelGroup?: string;
}): DiscountPriceType => {
  const special =
    getDiscountByModel(receiverDiscounts, modelGroup)?.special || 0;
  const findIn = (group?: string) =>
    sharerDiscounts
      .map((discounts) => getDiscountByModel(discounts, group))
      .find(Boolean);
  const sharer = findIn(modelGroup) ?? findIn(sharerModelGroup);
  const oldCustomer = sharer?.oldCustomer || 0;

  return { special, oldCustomer, total: special + oldCustomer };
};

/** Group the campaign models by `modelGroup` for the `Select` field. */
export const createModelOptionGroups = (
  models: CampaignModel[]
): SelectOptionGroup[] => {
  const groups = new Map<string, SelectOptionGroup>();

  for (const item of models) {
    const key = item.modelGroup;
    if (!groups.has(key)) {
      groups.set(key, { group: MODEL_GROUP_LABEL[key] ?? key, options: [] });
    }
    groups.get(key)?.options.push({
      label: item.model,
      value: item.modelCode,
    });
  }

  return [...groups.values()];
};

export const isNonOMO = (branchCode: string) => {
  return NON_OMO_BRANCHES.includes(branchCode);
};

export const createLeadRemark = (
  discount?: DiscountPriceType | null,
  modelName?: string,
  fromFriend: boolean = false,
  tier?: TierType | null
) => {
  const source = fromFriend
    ? `${CAMPAIGN_NAME}_family share`
    : `${CAMPAIGN_NAME}_self register_Web`;

  if (!discount) {
    return `${source}\nรุ่นที่สนใจ: ${modelName || '-'}`;
  }

  return [
    source,
    `รุ่นที่สนใจ: ${modelName || '-'} [ส่วนลด] ${formatPriceThai(discount.special || 0)}`,
    !!tier
      ? `[ส่วนลดลูกค้า my-MEMBER] ${formatPriceThai(discount.oldCustomer || 0)}`
      : '',
    `รวม: ${formatPriceThai(discount.total || 0)}`,
  ].join('\n');
};

export const REQUEST_TIMEOUT_MS = 15000;

export class RequestTimeoutError extends Error {}

export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number = REQUEST_TIMEOUT_MS
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new RequestTimeoutError()), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

export const markNow = () => performance.now();

export const msSince = (from?: number) => {
  if (from === undefined) return undefined;
  return Math.round(performance.now() - from);
};
